/**
 * utils.js — formateo, toast, modal, debounce, helpers visuales.
 */
// `window.X = …` y no `const X = …`, y los nueve módulos lo hacen igual desde el
// 2026-10-01. Un `const` de nivel superior queda en el entorno léxico global y
// **NO en `window`**, y esa asimetría —cinco módulos en `window`, cuatro no— ya
// costó dos defectos mudos: el botón de Apariencia del armazón, que preguntaba
// por `window.Theme?.openPanel?.()` y nunca hacía nada, y el confeti del final
// del recorrido, guardado con `if (window.Utils)`, que es siempre falso.
//
// El identificador suelto sigue resolviendo igual —es una propiedad del objeto
// global—, así que las 450 referencias `Utils.*`, las 54 de `Auth` y las 9 de
// `API` no se tocan, ni `globals.d.ts`.
//
// Y es el paso previo de la capa C: cuando estos módulos entren al bundle como
// módulos ES, `const X` pasa a ser de módulo y esas referencias revientan en
// tiempo de ejecución sin que TypeScript avise. Con la asignación a `window` no.
window.Utils = (() => {

  // ── Toast ──────────────────────────────────────────────────────────
  function toast(message, type = 'info', duration = 3500) {
    const container = document.getElementById('toast-container');
    if (!container) return;
    const el = document.createElement('div');
    const icons = { success: 'bi-check-circle-fill', error: 'bi-x-circle-fill',
                  warning: 'bi-exclamation-triangle-fill', info: 'bi-info-circle-fill' };
    el.className = `toast ${type}`;
    // El mensaje va como TEXTO, no como marcado, y no es una precaución
    // teórica: esta línea era `el.innerHTML = \`…<span>${message}</span>\`` con
    // `${message}` interpolado en crudo, y hay un camino completo desde una
    // entrada SIN SESIÓN hasta aquí:
    //
    //   data_forms.py:380   el formulario público crea el User con el
    //                       `full_name` que escribe quien lo rellena por token
    //   admin.py:1980/1988  compone «{full_name} ya completó…» como `message`
    //   TablaDeInduccion.tsx:184   Utils.toast(r.message, …)
    //   aquí               → innerHTML en el navegador de un administrador
    //
    // Ninguna prueba podía verlo, y por dos agujeros a la vez: `test_escapado`
    // solo barre `app/static/js/`, así que los 357 avisos de la capa React le
    // son invisibles; y aquí dentro, donde sí barre, su lista `TEXTO` exige un
    // punto antes del nombre (`.message`) y esto es `${message}`, un local
    // pelado — el punto ciego que su propia cabecera documenta.
    //
    // Con `textContent` el escapado deja de ser algo que haya que acordarse de
    // hacer en 371 sitios: no se puede pasar marcado aunque se quiera. Ninguna
    // llamada lo hacía (comprobado), y `test_iconos_contexto.py` ya prohibía
    // meter un icono por aquí.
    const marca = document.createElement('span');
    const icono = document.createElement('i');
    icono.className = `bi ${icons[type] || 'bi-info-circle-fill'}`;
    marca.appendChild(icono);
    const texto = document.createElement('span');
    texto.textContent = message;
    el.append(marca, texto);
    container.appendChild(el);
    setTimeout(() => {
      el.style.animation = 'slideOutRight .3s ease forwards';
      setTimeout(() => el.remove(), 300);
    }, duration);
  }

  // ── Modal ──────────────────────────────────────────────────────────
  // Protección contra pérdida de información (revisión 2026-07 · Fase 2.6):
  // cualquier input/select/textarea dentro de un modal abierto marca
  // window._appFormDirty=true. El router (app.js) lo consulta antes de
  // navegar a otra sección y pregunta antes de descartar cambios. Un solo
  // listener delegado cubre TODOS los formularios que ya usan Utils.modal()
  // (la gran mayoría de "crear/editar X" del sistema) sin tocar cada página.
  document.addEventListener('input', e => {
    if (e.target.closest('#modal-body')) window._appFormDirty = true;
  });
  document.addEventListener('change', e => {
    if (e.target.closest('#modal-body')) window._appFormDirty = true;
  });

  // ── Modal accesible ────────────────────────────────────────────────
  // Quién tenía el foco cuando se abrió. Al cerrar se le devuelve: sin esto el
  // foco cae al principio del documento y hay que recorrer la página entera
  // para volver al botón que se acaba de pulsar.
  let _modalOpener = null;

  // La promesa de la pregunta de descarte mientras está en el aire, o null.
  // La consulta `_teclaModal` para cederle el teclado — ver ahí.
  let _preguntaAbierta = null;

  const _SEL_FOCO = 'a[href], button:not([disabled]), input:not([disabled]),' +
    ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function _focosDelModal() {
    const m = document.getElementById('modal');
    return m ? [...m.querySelectorAll(_SEL_FOCO)].filter(el => el.offsetParent !== null) : [];
  }

  /** Mientras el modal está abierto, Tab no se sale de él y Escape lo cierra.
   *
   *  Sin la trampa, tabular desde el último control lleva el foco a la página
   *  de detrás —que el velo tapa— y no hay ninguna forma visible de volver: el
   *  usuario de teclado se queda recorriendo una pantalla que no ve. Era la peor
   *  barrera de accesibilidad del portal, y estaba justo en la pieza que usan
   *  las 25 páginas.
   *
   *  Escucha en fase de captura para llegar antes que cualquier handler de la
   *  página de debajo. */
  function _teclaModal(e) {
    // Mientras la pregunta de descarte está abierta, las teclas son suyas.
    // Sin esto, Escape llegaría también aquí y cerraría el modal de debajo —
    // que es exactamente lo que se está preguntando si cerrar. Este handler
    // está registrado ANTES que el de la pregunta y en la misma fase, así que
    // dispara primero: hay que salir por las buenas, no confiar en detenerlo
    // desde el otro lado.
    if (_preguntaAbierta) return;
    if (e.key === 'Escape') { e.preventDefault(); closeModalGuarded(); return; }
    if (e.key !== 'Tab') return;
    const f = _focosDelModal();
    if (!f.length) { e.preventDefault(); return; }
    const primero = f[0], ultimo = f[f.length - 1];
    if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
  }

  /** Cierre "silencioso" —Escape, la ✕ o clic en el velo— con la guarda de
   *  formulario sin guardar. Un botón "Cancelar" dentro del formulario sigue
   *  cerrando sin preguntar: eso ya es una decisión consciente del usuario.
   *
   *  Es `async` desde el 2026-09-10, cuando la pregunta dejó de ser el
   *  `confirm()` del navegador. Sus tres llamadores —Escape, la ✕ y el velo—
   *  no usan el valor de retorno, así que para ellos no cambia nada. */
  async function closeModalGuarded() {
    if (window._appFormDirty && !(await confirmarDescartarCambios())) return;
    window._appFormDirty = false;
    closeModal();
  }

  function modal({ title, body, footer = '', size = '' }) {
    const overlay = document.getElementById('modal-overlay');
    _modalOpener = document.activeElement;
    // innerHTML: los títulos llevan íconos (<i class="bi ...">) en varias páginas
    document.getElementById('modal-title').innerHTML = title;
    document.getElementById('modal-body').innerHTML = body;
    document.getElementById('modal-footer').innerHTML = footer;
    // Se asigna siempre, aunque venga vacío: antes solo se ponía cuando había
    // `size`, así que un modal ancho dejaba el ancho puesto en el elemento y el
    // siguiente —que no pide tamaño— salía igual de ancho sin haberlo pedido.
    document.getElementById('modal').style.maxWidth = size || '';
    overlay.classList.remove('hidden');
    window._appFormDirty = false;   // el modal recién abierto empieza "limpio"

    document.addEventListener('keydown', _teclaModal, true);
    // Se enfoca el primer CAMPO, no el primer elemento enfocable: ese sería la
    // ✕, y entonces lo primero que oye quien usa lector de pantalla al abrir un
    // formulario es "Cerrar". Si el modal no tiene campos —una confirmación,
    // por ejemplo— el foco va al propio diálogo, que anuncia su título.
    const campo = document.querySelector('#modal-body input:not([type=hidden]):not([disabled]),' +
      ' #modal-body select:not([disabled]), #modal-body textarea:not([disabled])');
    (campo || document.getElementById('modal')).focus();
  }

  function closeModal() {
    document.getElementById('modal-overlay').classList.add('hidden');
    document.removeEventListener('keydown', _teclaModal, true);
    window._appFormDirty = false;   // se cierra (guardado o cancelado): ya no hay nada que proteger
    // `document.contains` porque el elemento que abrió el modal puede haber
    // desaparecido mientras estaba abierto: es lo normal cuando guardar vuelve
    // a pintar la fila desde la que se abrió.
    if (_modalOpener && document.contains(_modalOpener)) _modalOpener.focus();
    _modalOpener = null;
  }

  // ── La pregunta de descartar cambios ───────────────────────────────
  //
  // Hasta el 2026-09-10 esto era `confirm()` del navegador, en dos sitios
  // (`utils.js` y `app.js:146`) con el mismo texto copiado. `CLAUDE.md` lo
  // prohíbe por nombre, y conviene escribir por qué el reemplazo obvio NO
  // valía, porque quien lo intente se va a encontrar lo mismo:
  //
  //   · `Utils.confirm` reutiliza `#modal-overlay`, que aquí es EL MODAL QUE SE
  //     ESTÁ INTENTANDO CERRAR. Se pisaría a sí mismo.
  //   · Y es asíncrono con callback, mientras que los dos sitios que preguntan
  //     necesitan un sí/no para decidir si siguen o revierten el hash.
  //
  // Por eso esta pieza es nueva y no una llamada más: monta su propio velo,
  // encima del modal (`z-index` mayor), y devuelve una promesa.
  //
  // Lo que NO sustituye: el `beforeunload` de `app.js`. Ahí el navegador impone
  // su propio diálogo, no acepta texto propio y no hay forma de reemplazarlo.
  //
  /** Pregunta de sí/no que puede abrirse ENCIMA de un modal.
   *
   *  @param {object} o
   *  @param {string} o.titulo    encabezado del diálogo
   *  @param {string} o.mensaje   el cuerpo; los saltos de línea hacen párrafos
   *  @param {string} [o.confirmar] etiqueta de la acción que sigue adelante
   *  @param {string} [o.cancelar]  etiqueta de la que se echa atrás
   *  @param {boolean} [o.peligro]  pinta de rojo la de seguir adelante
   *  @returns {Promise<boolean>} lo que eligió quien lo vio
   */
  function preguntar({ titulo, mensaje, confirmar = 'Continuar', cancelar = 'Cancelar', peligro = false }) {
    // Dos pulsaciones seguidas no abren dos diálogos: la segunda llamada se
    // engancha a la que ya está en el aire y las dos reciben la misma
    // respuesta. Sin esto, Escape repetido apilaría velos.
    if (_preguntaAbierta) return _preguntaAbierta;

    _preguntaAbierta = new Promise(resolver => {
      const quienPreguntaba = document.activeElement;
      const velo = document.createElement('div');
      velo.className = 'modal-overlay esta-encima';
      // Todo lo que viene de fuera va escapado: `mensaje` y las etiquetas son
      // parámetros, y una pregunta que interpole texto de usuario sin escapar
      // sería la misma clase de defecto que este proyecto persigue en las 25
      // páginas. Los saltos de línea se convierten en párrafos DESPUÉS de
      // escapar, que es el único orden correcto.
      const parrafos = String(mensaje).split(/[\r\n]+/).filter(Boolean)
        .map(p => `<p>${escapeHtml(p)}</p>`).join('');
      velo.innerHTML = `
        <div class="modal" role="alertdialog" aria-modal="true" tabindex="-1"
             aria-labelledby="pregunta-titulo" aria-describedby="pregunta-texto"
             style="max-width:440px">
          <div class="modal-header" id="pregunta-titulo">${escapeHtml(titulo)}</div>
          <div class="modal-body" id="pregunta-texto">${parrafos}</div>
          <div class="modal-footer">
            <button class="btn btn-ghost" data-respuesta="no">${escapeHtml(cancelar)}</button>
            <button class="btn ${peligro ? 'btn-danger' : 'btn-primary'}"
                    data-respuesta="si">${escapeHtml(confirmar)}</button>
          </div>
        </div>`;

      const responder = respuesta => {
        document.removeEventListener('keydown', _tecla, true);
        velo.remove();
        _preguntaAbierta = null;
        // El foco vuelve a quien preguntaba —el campo, o el botón— y no al
        // principio del documento. `contains` porque el modal de debajo puede
        // haberse cerrado mientras tanto.
        if (quienPreguntaba && document.contains(quienPreguntaba)) quienPreguntaba.focus();
        resolver(respuesta);
      };

      function _tecla(e) {
        // Trampa de foco propia: mientras esto está abierto, Tab no sale de
        // aquí. `_teclaModal` ya se apartó (mira `_preguntaAbierta`), así que
        // sin esta trampa el Tab se iría al modal de debajo, que el velo tapa.
        if (e.key === 'Escape') { e.preventDefault(); responder(false); return; }
        if (e.key !== 'Tab') return;
        const f = [...velo.querySelectorAll('button')];
        const primero = f[0], ultimo = f[f.length - 1];
        if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
        else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
      }

      velo.addEventListener('click', e => {
        // Un clic en el velo NO responde nada, a diferencia de los modales
        // normales: esto es una pregunta, y contestarla en silencio por un clic
        // fuera sería la misma trampa que se está intentando evitar. Hay que
        // elegir, o pulsar Escape, que es un «no» explícito.
        const btn = e.target.closest('[data-respuesta]');
        if (btn) responder(btn.dataset.respuesta === 'si');
      });
      document.addEventListener('keydown', _tecla, true);

      document.body.appendChild(velo);
      // El foco arranca en la opción que NO destruye nada: quien pulse Intro
      // por inercia no pierde lo que escribió.
      velo.querySelector('[data-respuesta="no"]').focus();
    });

    return _preguntaAbierta;
  }

  /** El caso concreto que sustituyó a los `confirm()` nativos del shell.
   *  Vive aquí y no copiado en `app.js` para que las cuatro formas de descarte
   *  —navegar, Escape, la ✕ y el velo— pregunten exactamente lo mismo. */
  function confirmarDescartarCambios() {
    return preguntar({
      titulo: 'Tienes cambios sin guardar',
      mensaje: 'Si sales ahora se pierde lo que escribiste en este formulario.',
      confirmar: 'Salir sin guardar',
      cancelar: 'Seguir editando',
      peligro: true,
    });
  }

  // ── Date formatting ────────────────────────────────────────────────
  function fmtDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric' });
  }
  /**
   * Fecha SIN hora (`yyyy-mm-dd`, como las columnas DATE del backend).
   *
   * `fmtDate` la pasa por `new Date(...)`, que interpreta un `yyyy-mm-dd` suelto
   * como medianoche UTC y luego la imprime en la zona local: en Colombia
   * (UTC-5) "2026-12-31" se muestra como "30 de dic". Para una fecha límite de
   * un curso eso es un día de diferencia en la cara del colaborador. Aquí se
   * construye la fecha con los componentes locales, sin conversión de zona.
   */
  function fmtDateOnly(d) {
    if (!d) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d).trim());
    if (!m) return fmtDate(d);   // trae hora: fmtDate ya es correcto
    const [, y, mes, dia] = m;
    return new Date(+y, +mes - 1, +dia)
      .toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  function fmtDatetime(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('es-CO', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
  }
  function relativeTime(d) {
    if (!d) return '';
    const diff = Date.now() - new Date(d);
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return 'hace un momento';
    if (mins < 60) return `hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7)  return `hace ${days} días`;
    return fmtDate(d);
  }

  // ── Avatar initials ────────────────────────────────────────────────
  function avatarInitials(name = '') {
    const parts = name.trim().split(' ').filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
  }

  // Color determinístico por nombre (paleta agradable estilo Slack/Notion)
  const _AVATAR_PALETTE = [
    ['#1abc9c','#fff'], ['#2ecc71','#fff'], ['#3498db','#fff'], ['#9b59b6','#fff'],
    ['#e67e22','#fff'], ['#e74c3c','#fff'], ['#16a085','#fff'], ['#27ae60','#fff'],
    ['#2980b9','#fff'], ['#8e44ad','#fff'], ['#f39c12','#fff'], ['#d35400','#fff'],
    ['#c0392b','#fff'], ['#0d2b4e','#F5A623'], ['#34495e','#fff'], ['#7f8c8d','#fff'],
  ];
  function avatarColor(name = '') {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return _AVATAR_PALETTE[Math.abs(hash) % _AVATAR_PALETTE.length];
  }

  /** Pinta un icono guardado en base de datos.
   *
   *  Ocho tablas guardan el icono de algo —una insignia, una comunidad, un tipo
   *  de solicitud, una base de datos…— en una columna de texto. Hasta el
   *  2026-09-01 esa columna era VARCHAR(10) y lo que se guardaba era **un emoji
   *  escrito a mano** en un input de `maxlength=4`, así que cada fila acababa con
   *  el pictograma que se le ocurrió a quien la creó, dibujado por la fuente del
   *  sistema operativo de quien la mira.
   *
   *  Ahora la columna admite un nombre de clase y el selector guarda `bi-*`. Pero
   *  las filas antiguas siguen teniendo su emoji, y hacer un UPDATE masivo sobre
   *  datos que alguien escribió a mano tiene más riesgo que beneficio. Así que
   *  esta función acepta las dos formas: si parece una clase, pinta un <i>; si no,
   *  pinta el carácter tal cual, escapado.
   *
   *  La conversión es gradual: cada fila se moderniza cuando alguien la edita. */
  // ── Selector de icono ────────────────────────────────────────────
  // Sustituye a los <input maxlength="4"> donde se escribía un emoji a mano.
  // Los ~2000 nombres de Bootstrap Icons ya viven en la hoja auto-hospedada,
  // así que se leen de ahí en vez de embarcar una lista que habría que mantener
  // sincronizada. Si la hoja no se puede leer, queda el juego de siempre.
  let _iconosCache = null;
  const _ICONOS_BASE = ['bi-app','bi-star','bi-trophy','bi-award','bi-people','bi-person',
    'bi-cake2','bi-calendar-event','bi-clock','bi-file-earmark-text','bi-folder','bi-tag',
    'bi-journal-text','bi-mortarboard','bi-play-circle','bi-camera-video','bi-tools','bi-gear',
    'bi-shield-lock','bi-heart-pulse','bi-lightbulb','bi-chat-square-text','bi-envelope','bi-bell',
    'bi-house-door','bi-building','bi-briefcase','bi-bank','bi-geo-alt','bi-database'];

  function listaIconos() {
    if (_iconosCache) return _iconosCache;
    const set = new Set();
    try {
      for (const hoja of document.styleSheets) {
        let reglas; try { reglas = hoja.cssRules; } catch { continue; }   // otro origen
        if (!reglas) continue;
        for (const r of reglas) {
          const m = r.selectorText && r.selectorText.match(/^\.(bi-[a-z0-9-]+)::before$/);
          if (m) set.add(m[1]);
        }
      }
    } catch {
      // Se traga porque hay respaldo: si recorrer las hojas falla, `_ICONOS_BASE`
      // de la línea siguiente da una lista corta pero utilizable. El selector de
      // iconos sale con menos opciones en vez de salir vacío.
    }
    _iconosCache = set.size > 40 ? [...set].sort() : _ICONOS_BASE;
    return _iconosCache;
  }

  /* `iconPicker()` y `wireIconPicker()` se fueron el 2026-09-24 con Documentos,
     su último consumidor: era la última pantalla vanilla con un formulario que
     eligiera icono. Lo sustituye `components/SelectorDeIcono.tsx`, que usa la
     misma `listaIconos()` de aquí abajo — por eso esa se queda. */

  // ── Texto enriquecido seguro (markdown ligero para el feed) ─────────
  function escapeHtml(s = '') {
    return String(s).replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  /**
   * Convierte texto plano del feed en HTML seguro:
   *  · escapa cualquier HTML (anti-XSS)
   *  · **negrita** → <strong>, *cursiva* / _cursiva_ → <em>
   *  · URLs http(s) → enlaces clicables
   *  · saltos de línea → <br>
   */
  function richText(str = '') {
    // El editor puede dejar &nbsp; literal en texto plano; decodificar a espacio
    // ANTES de escapar (si no, se vería el texto "&nbsp;"). Solo entidades benignas.
    str = String(str).replace(/&nbsp;|&#160;|&#xa0;/gi, ' ');
    let s = escapeHtml(str);
    s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');           // **negrita**
    s = s.replace(/(^|[^*\w])\*([^*\n]+)\*(?!\w)/g, '$1<em>$2</em>');     // *cursiva*
    s = s.replace(/(^|[^_\w])_([^_\n]+)_(?!\w)/g, '$1<em>$2</em>');       // _cursiva_
    s = s.replace(/(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener" style="color:var(--navy);text-decoration:underline">$1</a>');
    s = s.replace(/\n/g, '<br>');
    return s;
  }

  // ── Sanitizador de HTML enriquecido (whitelist, anti-XSS) ───────────
  const _ALLOWED_TAGS = { STRONG:1, B:1, EM:1, I:1, U:1, BR:1, A:1, SPAN:1, P:1, DIV:1 };
  function _isSafeColor(v) {
    return /^#[0-9a-f]{3,8}$/i.test(v.trim()) || /^rgb\([\d.,\s%]+\)$/i.test(v.trim()) || /^[a-z]{3,20}$/i.test(v.trim());
  }
  function _isSafeUrl(u) { return /^(https?:|mailto:)/i.test(u.trim()); }
  function _cleanEl(el) {
    const tag = el.tagName;
    // Chip de mención: conservar solo class="mention" y data-uid numérico
    if (tag === 'SPAN' && el.classList.contains('mention')) {
      const uid = el.getAttribute('data-uid');
      [...el.attributes].forEach(a => el.removeAttribute(a.name));
      el.className = 'mention';
      if (uid && /^\d+$/.test(uid)) el.setAttribute('data-uid', uid);
      return;
    }
    [...el.attributes].forEach(a => {
      const n = a.name.toLowerCase();
      if (tag === 'A' && n === 'href' && _isSafeUrl(a.value)) return;
      if (tag === 'SPAN' && n === 'style') {
        const m = /(?:^|;)\s*color\s*:\s*([^;]+)/i.exec(a.value);
        el.removeAttribute('style');
        if (m && _isSafeColor(m[1])) el.style.color = m[1].trim();
        return;
      }
      el.removeAttribute(a.name);   // descarta on*, class, etc.
    });
    if (tag === 'A') { el.setAttribute('target', '_blank'); el.setAttribute('rel', 'noopener noreferrer'); }
  }
  function sanitizeRich(html = '') {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    tpl.content.querySelectorAll('script,style,iframe,object,embed,link,meta,svg,form,input,button,img')
      .forEach(n => n.remove());
    // execCommand('foreColor') puede generar <font color="..."> en algunos navegadores.
    // Convertir a <span style="color:..."> (que sí se conserva) antes de sanear.
    tpl.content.querySelectorAll('font').forEach(f => {
      const span = document.createElement('span');
      const col = f.getAttribute('color');
      if (col && _isSafeColor(col)) span.style.color = col.trim();
      while (f.firstChild) span.appendChild(f.firstChild);
      f.replaceWith(span);
    });
    tpl.content.querySelectorAll('*').forEach(el => {
      if (!_ALLOWED_TAGS[el.tagName]) {
        const p = el.parentNode;
        if (p) { while (el.firstChild) p.insertBefore(el.firstChild, el); p.removeChild(el); }
      } else { _cleanEl(el); }
    });
    return tpl.innerHTML;
  }
  /** Render del contenido de un post: HTML del editor → saneado; texto plano → markdown. */
  function renderPostContent(raw = '') {
    if (/<(strong|b|em|i|u|span|a|br|div|p)\b/i.test(raw)) return sanitizeRich(raw);
    return richText(raw);
  }

  // ── Moderación: detección de lenguaje ofensivo ──────────────────────
  // Se compara por palabra normalizada (sin acentos, minúsculas, sin leet ni
  // repeticiones) para evitar falsos positivos.
  //
  // La lista VIVA la manda el servidor (`/admin/moderation/words/effective`),
  // que es la misma contra la que valida `find_offensive`. Esta copia local es
  // solo el RESPALDO hasta que llegue —o si la petición falla—: antes era la
  // única, así que lo que el admin añadía no llegaba nunca al navegador y el
  // usuario, en vez del aviso amable en el compositor, recibía un 400 seco al
  // pulsar Publicar. Y lo que el admin QUITABA seguía bloqueado aquí.
  let _BANNED = new Set([
    'puta','puto','putas','putos','gonorrea','malparido','malparida','hijueputa','hpta','hp',
    'marica','maricon','maricas','maricones','perra','zorra','imbecil','idiota','estupido','estupida',
    'pendejo','pendeja','cabron','cabrona','mierda','verga','vergas','pirobo','piroba','gamin','gamina',
    'culo','culiao','culero','joder','jodete','coño','chimba','chucha','careverga','carechimba',
    'guevon','huevon','gueva','bobohpta','sapo','sapa','rata','basura','asqueroso','asco','estupidos',
  ]);
  let _bannedCargada = false;

  /** Trae la lista vigente del servidor. Idempotente; falla en silencio al respaldo. */
  async function loadProfanityList() {
    if (_bannedCargada) return;
    _bannedCargada = true;   // no reintentar en bucle si el endpoint no responde
    try {
      const r = await API.get('/admin/moderation/words/effective');
      if (r?.words?.length) _BANNED = new Set(r.words.map(w => _normMod(w)));
    } catch { /* se queda el respaldo local */ }
  }
  function _normMod(text = '') {
    let t = String(text).normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
    t = t.replace(/[0@]/g, 'o').replace(/[1!|]/g, 'i').replace(/3/g, 'e').replace(/4/g, 'a').replace(/[5$]/g, 's').replace(/7/g, 't');
    return t;
  }
  /** Devuelve la primera palabra ofensiva encontrada, o null. Ignora etiquetas HTML. */
  function findProfanity(text = '') {
    const plain = String(text).replace(/<[^>]+>/g, ' ');
    const words = (_normMod(plain).match(/[a-zñ]+/gi)) || [];
    for (const w of words) {
      const w2 = w.replace(/(.)\1{2,}/g, '$1');  // "putaaaa" → "puta"
      if (_BANNED.has(w) || _BANNED.has(w2)) return w;
    }
    return null;
  }

  // El editor enriquecido vanilla (`richComposer`, `wireRichComposer`,
  // `getRich`, `clearRich`) se borró el 2026-09-08 al migrar Novedades: era su
  // único consumidor, y se quedaba a cero. Su equivalente React vive en
  // `features/novedades/piezas.tsx` (`EditorRico` / `leerEditor`) y llama a
  // `sanitizeRich` de aquí, que sigue siendo la única lista blanca del portal.
  //
  // Con él se fueron sus seis clases CSS —`.rich-toolbar`, `.rt-btn`,
  // `.rt-color`, `.rt-sep`, `.rt-colors`, `.rich-editor`— y
  // `.feed-composer-input`, que tampoco tenía otro consumidor.

  // ── Almacenamiento del navegador ───────────────────────────────────
  //
  // El 2026-09-10, al revisar el shell, apareció un patrón repetido en seis
  // sitios: **la LECTURA de `localStorage` estaba protegida y la ESCRITURA de
  // al lado no**.
  //
  //     function _getFavorites() { try { return JSON.parse(...) } catch { return [] } }
  //     function _setFavorites(l) { localStorage.setItem(FAV_KEY, ...) }   ← sin red
  //
  // No era descuido: el `try/catch` se puso para el `JSON.parse` —datos
  // corruptos—, no para `localStorage`. Así que protegía justo la mitad que no
  // hacía falta proteger.
  //
  // Hay que separar dos fallos distintos que se confundían en uno:
  //
  //  1. **El almacenamiento no está disponible** (política de empresa, modo
  //     privado antiguo). Entonces el portal NO PUEDE funcionar: la sesión vive
  //     ahí (`api.js` lee el token en cada petición). Eso no se tapa envolviendo
  //     escrituras — se comprueba una vez al arrancar y se dice. Lo hace
  //     `hayAlmacenamiento()`, que llama `app.js` antes de nada.
  //  2. **Una escritura concreta falla** con el cupo lleno, a mitad de sesión y
  //     con las lecturas funcionando. Eso sí se traga, porque lo que se guarda
  //     aquí son comodidades: un favorito, la sección abierta del menú, el
  //     tema. Que una no se persista no puede llevarse por delante el clic que
  //     la provocó.
  //
  /** ¿Se puede leer y escribir? Se prueba escribiendo de verdad: en varios
   *  navegadores `localStorage` existe y solo falla al usarlo. */
  function hayAlmacenamiento() {
    try {
      const sonda = '__sonda__';
      localStorage.setItem(sonda, '1');
      localStorage.removeItem(sonda);
      return true;
    } catch { return false; }
  }

  /** `localStorage` para comodidades: nunca lanza, y el respaldo es explícito. */
  const recordar = {
    /** Lee y parsea JSON. `siFalla` cubre las dos cosas que pueden ir mal:
     *  que no se pueda leer y que lo leído no sea JSON válido. */
    leer(clave, siFalla = null) {
      try {
        const crudo = localStorage.getItem(clave);
        return crudo === null ? siFalla : JSON.parse(crudo);
      } catch { return siFalla; }
    },
    /** Devuelve si se pudo guardar, por si alguien quiere avisar. Casi nadie
     *  querrá: son comodidades. */
    guardar(clave, valor) {
      try { localStorage.setItem(clave, JSON.stringify(valor)); return true; }
      catch { return false; }
    },
    olvidar(clave) {
      try { localStorage.removeItem(clave); return true; } catch { return false; }
    },
  };

  // ── Debounce ───────────────────────────────────────────────────────
  function debounce(fn, ms = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  }

  // ── Confirm dialog ─────────────────────────────────────────────────
  function confirm(title, message, onConfirm, danger = false) {
    modal({
      title,
      body: `<p>${message}</p>`,
      footer: `
        <button class="btn btn-ghost" onclick="Utils.closeModal()">Cancelar</button>
        <button class="btn ${danger ? 'btn-danger' : 'btn-primary'}" id="confirm-ok">${danger ? 'Eliminar' : 'Confirmar'}</button>`,
    });
    setTimeout(() => {
      document.getElementById('confirm-ok')?.addEventListener('click', () => {
        closeModal();
        onConfirm();
      });
    }, 50);
  }

  // ── Skeleton loader ────────────────────────────────────────────────
  // ── Empty state (ilustración SVG suave con color del tema) ──────────
  //
  // `compact` (revisión 2026-08-28): la versión normal reserva 4rem de relleno y
  // una ilustración de 110px. Es lo correcto cuando ocupa la pantalla, y
  // desproporcionado dentro de una tarjeta lateral donde el mensaje entero es
  // «Sin reconocimientos aún» — ahí el vacío pesaba más que el contenido de las
  // tarjetas llenas. El parámetro es opcional y va al final, así que las cerca de
  // cien llamadas que ya existen no cambian de comportamiento.
  /** Lo que se enseña cuando algo falló.
   *
   *  Hoy 45 sitios resuelven esto con `<div class="alert alert-error">` y el
   *  mensaje crudo del servidor. No dice qué se estaba cargando ni qué hacer:
   *  el tablero Estados lo llama «un encogimiento de hombros», y acierta.
   *
   *  **Tres causas, tres salidas, y a propósito no comparten botón:**
   *
   *  | Causa | Qué sirve | Qué NO sirve |
   *  |---|---|---|
   *  | El servidor no respondió | Reintentar | — |
   *  | No tienes permiso (403) | Pedir acceso | Reintentar: dará 403 otra vez |
   *  | No existe o se borró (404) | Volver | Reintentar: seguirá sin existir |
   *
   *  Ofrecer «Reintentar» en las tres enseña a desconfiar del botón, que es
   *  peor que no ponerlo.
   *
   *  El detalle técnico va discreto y al final: sirve para reportar, no para
   *  leer. Y nunca se culpa a quien mira.
   *
   *  @param err        el Error capturado. `err.status` lo pone `api.js`.
   *  @param que        qué se estaba cargando, en minúscula: "tus cursos".
   *  @param reintentar JS a ejecutar en el botón. Solo se usa si la causa lo admite.
   *  @param volver     {texto, js} para el caso de que no exista.
   */
  function errorState(err, { que = 'la información', reintentar = '', volver = null } = {}) {
    const status = err && err.status;
    const sinRed = typeof navigator !== 'undefined' && navigator.onLine === false;
    const causa = sinRed ? 'red' : status === 403 || status === 401 ? 'permiso'
                : status === 404 ? 'noExiste' : 'servidor';

    const CAUSAS = {
      red: {
        icono: 'bi-wifi-off', color: 'var(--orange-text)',
        rotulo: 'Estás sin conexión',
        desc: `No se pudo cargar ${que} porque no hay red. Lo que ya estaba en pantalla sigue ahí.`,
      },
      servidor: {
        icono: 'bi-exclamation-triangle', color: 'var(--red-text)',
        rotulo: `No pudimos cargar ${que}`,
        desc: 'La conexión con el servidor se interrumpió. Tus datos están a salvo.',
      },
      permiso: {
        icono: 'bi-shield-lock', color: 'var(--blue-text)',
        rotulo: 'Esto no está en tu rol',
        desc: `Tu rol no incluye ${que}. Si lo necesitas para tu trabajo, pídeselo a Bienestar.`,
      },
      noExiste: {
        icono: 'bi-question-circle', color: 'var(--text-muted)',
        rotulo: 'Eso ya no está',
        desc: 'Puede que lo hayan retirado, o que el enlace esté viejo.',
      },
    };
    const c = CAUSAS[causa];

    // Reintentar solo donde arregla algo.
    let boton = '';
    if ((causa === 'servidor' || causa === 'red') && reintentar) {
      boton = `<button type="button" class="btn btn-outline btn-sm" onclick="${escapeHtml(reintentar)}">
        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i> Reintentar</button>`;
    } else if (causa === 'noExiste' && volver) {
      boton = `<button type="button" class="btn btn-outline btn-sm" onclick="${escapeHtml(volver.js)}">
        <i class="bi bi-arrow-left" aria-hidden="true"></i> ${escapeHtml(volver.texto)}</button>`;
    }

    // El dato técnico, para quien lo tenga que reportar. Escapado: el mensaje
    // lo escribe el servidor y acaba en innerHTML.
    const hora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
    const tecnico = (status || (err && err.message))
      ? `<p class="error-state-tecnico">${status ? `HTTP ${status}` : 'Sin respuesta'} · ${hora}${
          err && err.message ? ` · ${escapeHtml(err.message)}` : ''}</p>`
      : '';

    return `<div class="empty-state error-state">
      <div class="empty-state-illustration">
        <svg viewBox="0 0 120 120" width="110" height="110" aria-hidden="true">
          <circle cx="60" cy="60" r="52" fill="${c.color}" opacity="0.08"/>
          <circle cx="60" cy="60" r="38" fill="${c.color}" opacity="0.06"/>
        </svg>
        <div class="empty-state-icon" style="color:${c.color}"><i class="bi ${c.icono}"></i></div>
      </div>
      <div class="empty-state-title">${c.rotulo}</div>
      <p class="empty-state-desc">${c.desc}</p>
      ${boton ? `<div style="margin-top:1rem">${boton}</div>` : ''}
      ${tecnico}
    </div>`;
  }

  // ── Barra de carga superior (estilo NProgress) ──────────────────────
  let _loaderTimer = null;
  function loaderStart() {
    let bar = document.getElementById('top-loader');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'top-loader';
      document.body.appendChild(bar);
    }
    clearTimeout(_loaderTimer);
    bar.style.transition = 'none';
    bar.style.width = '0';
    bar.style.opacity = '1';
    // forzar reflow
    void bar.offsetWidth;
    bar.style.transition = 'width .4s ease';
    bar.style.width = '70%';
  }
  function loaderDone() {
    const bar = document.getElementById('top-loader');
    if (!bar) return;
    bar.style.width = '100%';
    _loaderTimer = setTimeout(() => {
      bar.style.opacity = '0';
      bar.style.width = '0';
    }, 300);
  }

  // ── Confetti (canvas, sin dependencias) ─────────────────────────────
  function confetti(opts = {}) {
    const count = opts.count || 120;
    const colors = opts.colors || ['#F5A623','#0D2B4E','#27AE60','#2980B9','#E74C3C','#8E44AD'];
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:99999';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const W = canvas.width = window.innerWidth;
    const H = canvas.height = window.innerHeight;
    const parts = Array.from({ length: count }, () => ({
      x: W / 2 + (Math.random() - 0.5) * 200,
      y: H / 3 + (Math.random() - 0.5) * 60,
      vx: (Math.random() - 0.5) * 12,
      vy: Math.random() * -15 - 5,
      size: Math.random() * 8 + 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      rot: Math.random() * 360,
      vrot: (Math.random() - 0.5) * 20,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
    }));
    let frame = 0;
    const gravity = 0.5;
    (function animate() {
      ctx.clearRect(0, 0, W, H);
      parts.forEach(p => {
        p.vy += gravity;
        p.x += p.vx; p.y += p.vy; p.rot += p.vrot;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot * Math.PI / 180);
        ctx.fillStyle = p.color;
        if (p.shape === 'rect') ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        else { ctx.beginPath(); ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2); ctx.fill(); }
        ctx.restore();
      });
      frame++;
      if (frame < 140) requestAnimationFrame(animate);
      else canvas.remove();
    })();
  }

  // ── Lightbox ───────────────────────────────────────────────────────
  let _lightboxImages = [], _lightboxIdx = 0;
  function openLightbox(images, idx = 0) {
    _lightboxImages = images;
    _lightboxIdx = idx;
    _renderLightboxStrip();
    _showLightboxImage();
    document.getElementById('lightbox').classList.remove('hidden');
  }
  function _renderLightboxStrip() {
    const strip = document.getElementById('lightbox-strip');
    if (!strip) return;
    if (_lightboxImages.length < 2) { strip.innerHTML = ''; strip.style.display = 'none'; return; }
    strip.style.display = 'flex';
    strip.innerHTML = _lightboxImages.map((im, i) =>
      `<img src="${(im.thumb_url || im.url || im)}" data-i="${i}"
        class="lb-thumb ${i===_lightboxIdx?'active':''}"
        onclick="Utils._lbGo(${i})" />`).join('');
  }
  function _lbGo(i) { _lightboxIdx = i; _showLightboxImage(); }
  function _showLightboxImage() {
    const img = _lightboxImages[_lightboxIdx];
    document.getElementById('lightbox-img').src = img.url || img;
    document.getElementById('lightbox-caption').textContent = img.caption || '';
    const counter = document.getElementById('lightbox-counter');
    if (counter) counter.textContent = `${_lightboxIdx + 1} / ${_lightboxImages.length}`;
    document.querySelectorAll('#lightbox-strip .lb-thumb').forEach((t, i) =>
      t.classList.toggle('active', i === _lightboxIdx));
  }
  function lightboxNext() {
    _lightboxIdx = (_lightboxIdx + 1) % _lightboxImages.length;
    _showLightboxImage();
  }
  function lightboxPrev() {
    _lightboxIdx = (_lightboxIdx - 1 + _lightboxImages.length) % _lightboxImages.length;
    _showLightboxImage();
  }

  // ── Descarga autenticada (envía el JWT, para endpoints protegidos) ──
  /** Extrae el nombre de archivo de la cabecera Content-Disposition.
   *  Soporta filename*=UTF-8''… (con acentos) y el filename= simple. */
  function _nombreDeCabecera(res) {
    const cd = res.headers.get('Content-Disposition') || '';
    const ext = cd.match(/filename\*=UTF-8''([^;]+)/i);
    if (ext) { try { return decodeURIComponent(ext[1].trim()); } catch { /* sigue */ } }
    const simple = cd.match(/filename="?([^";]+)"?/i);
    return simple ? simple[1].trim() : null;
  }

  async function downloadAuthed(url, filename) {
    try {
      toast('Generando archivo...', 'info', 1500);
      const res = await fetch(url.startsWith('/api') ? url : `/api${url}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('No se pudo descargar');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      // Si quien llama no impone un nombre, se respeta el que envía el servidor
      // en Content-Disposition: ahí ya se arma con el nombre real del reporte y
      // la fecha, en vez del genérico "descarga".
      a.download = filename || _nombreDeCabecera(res) || 'descarga';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast(e.message || 'Error al descargar', 'error'); }
  }

  // ── Autocompletar de colaborador para MI ficha ───────────────────────────
  //
  // Una columna de tipo Empleado la puede llenar el colaborador (p. ej. "padrino
  // de inducción"). Vive aquí, y no en cada pantalla, porque la necesitan DOS —el
  // formulario de datos iniciales y Mi ficha— y este módulo ya arrastra el
  // historial de qué pasa cuando la misma regla se implementa dos veces.
  //
  // No reutiliza el de Recursos Privados: aquel pega a
  // /private-records/autocomplete/employees, detrás del permiso de staff, y el
  // formulario de datos iniciales corre mientras el candado bloquea todo lo demás.
  // Este usa /auth/colaboradores, que además comprueba que la columna sea de verdad
  // suya antes de devolver nombres.
  function campoEmpleadoHtml(id, valor, idValor) {
    const v = escapeHtml(valor == null ? '' : String(valor));
    return `<div style="position:relative">
      <input class="form-input" id="${id}" value="${v}" autocomplete="off"
             placeholder="Escribe un nombre...">
      <input type="hidden" id="${id}-id" value="${idValor || ''}">
      <div id="${id}-ac" class="ac-lista" style="display:none"></div>
    </div>`;
  }

  function campoEmpleadoWire(id, campoKey) {
    const input = document.getElementById(id);
    const oculto = document.getElementById(`${id}-id`);
    const caja = document.getElementById(`${id}-ac`);
    if (!input || !caja) return;
    // Editar el texto a mano invalida la selección previa: vuelve a ser "solo un
    // nombre" hasta que se elija una sugerencia otra vez.
    input.addEventListener('input', () => { if (oculto) oculto.value = ''; });
    input.addEventListener('input', debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { caja.style.display = 'none'; return; }
      let res;
      try { res = await API.get('/auth/colaboradores', { campo: campoKey, q }); }
      catch { caja.style.display = 'none'; return; }
      caja.style.display = 'block';
      caja.innerHTML = res.length
        ? res.map(u => `<div class="ac-item" data-id="${u.id}" data-nombre="${escapeHtml(u.label)}">
             ${escapeHtml(u.label)}${u.sub ? ` · <span style="color:var(--text-muted)">${escapeHtml(u.sub)}</span>` : ''}
           </div>`).join('')
        : '<div class="ac-vacio">Sin resultados</div>';
      caja.querySelectorAll('.ac-item').forEach(el => el.addEventListener('click', () => {
        input.value = el.dataset.nombre;
        if (oculto) oculto.value = el.dataset.id;
        caja.style.display = 'none';
      }));
    }, 250));
    document.addEventListener('click', e => {
      if (!e.target.closest(`#${id}`) && !e.target.closest(`#${id}-ac`)) caja.style.display = 'none';
    });
  }

  // Seis de estos se fueron el 2026-09-30 sin que nada fallara: `ring`,
  // `avatarEl`, `statusBadge`, `countUp`, `donut` y `filterEmptyState`. Ninguno
  // tenía un solo consumidor —ni en React, ni en el resto del vanilla, ni en los
  // tres HTML—, y el historial (`git log -S`) dice cómo llegó cada uno a eso:
  //
  //   · `ring`, `avatarEl`, `countUp` y `donut` **no lo tuvieron nunca**: cero
  //     commits en los 229 visibles tocan `Utils.<nombre>`.
  //   · `filterEmptyState` tampoco, y es el caso más llamativo: se escribió el
  //     2026-09-03 con su registro de cambios, y `Utils.filterEmptyState` solo
  //     ha existido nunca DENTRO de ese registro. Se documentó una ayuda que no
  //     se llegó a llamar.
  //   · `statusBadge` sí tuvo dos —`page-admin.js` y `page-notifications.js`— y
  //     se quedó sin ellos al migrarse esas pantallas a React. Es la clase de
  //     muerte que produce un rediseño y que nadie ve, porque el archivo que
  //     muere no es el que se estaba tocando.
  //
  // Lo vigila ahora `tests/test_utils_sin_consumidor.py`, para que no vuelvan a
  // acumularse; el detalle está en
  // `docs/Cambios/2026-09-30-utils-seis-exports-muertos.md`.
  //
  // `richText` era el séptimo candidato y NO se fue: `renderPostContent` lo
  // llama aquí arriba, así que el cuerpo está vivo. Lo que no tiene consumidor
  // es su EXPORT, y retirarlo es otra decisión — queda anotado con su motivo en
  // las excepciones de esa prueba, no borrado a la callada.
  return {
    toast, modal, closeModal, closeModalGuarded, confirmarDescartarCambios,
    preguntar, hayAlmacenamiento, recordar,
    campoEmpleadoHtml, campoEmpleadoWire,
    fmtDate, fmtDateOnly, fmtDatetime, relativeTime,
    avatarInitials, avatarColor,
    listaIconos,
    confirm, openLightbox, lightboxNext, lightboxPrev, _lbGo,
    loaderStart, loaderDone, confetti,
    downloadAuthed, escapeHtml, sanitizeRich, renderPostContent, findProfanity,
    errorState,
    loadProfanityList,
  };
})();

// Inicializar lightbox controls
document.addEventListener('DOMContentLoaded', () => {
  document.getElementById('lightbox-close')?.addEventListener('click', () =>
    document.getElementById('lightbox').classList.add('hidden'));
  document.getElementById('lightbox-next')?.addEventListener('click', () => Utils.lightboxNext());
  document.getElementById('lightbox-prev')?.addEventListener('click', () => Utils.lightboxPrev());
  document.addEventListener('keydown', e => {
    const lb = document.getElementById('lightbox');
    if (lb.classList.contains('hidden')) return;
    if (e.key === 'Escape') lb.classList.add('hidden');
    if (e.key === 'ArrowRight') Utils.lightboxNext();
    if (e.key === 'ArrowLeft')  Utils.lightboxPrev();
  });
  // Modal close — revisión 2026-07-27: cerrar con el botón "✕" o haciendo clic
  // afuera (el fondo del modal) son las dos formas de descarte "silencioso"
  // que el guard de la Fase 2.6 NO cubría (ese solo protege navegar a otra
  // sección o cerrar la pestaña; estos dos casos llamaban a closeModal()
  // directo). Un botón explícito "Cancelar" dentro del formulario sigue
  // cerrando sin preguntar — esos ya son una decisión consciente del usuario.
  // La guarda vive ahora dentro del IIFE (Utils.closeModalGuarded) para que las
  // TRES formas de descarte silencioso —la ✕, el clic en el velo y la tecla
  // Escape— pregunten exactamente lo mismo. Antes Escape no existía y las otras
  // dos llevaban su propia copia de la pregunta aquí.
  document.getElementById('modal-close')?.addEventListener('click', Utils.closeModalGuarded);
  document.getElementById('modal-overlay')?.addEventListener('click', e => {
    if (e.target.id === 'modal-overlay') Utils.closeModalGuarded();
  });
});
